import React, { useEffect, useRef, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import useAmanFlow from "../../../hooks/useAmanFlow";
import { clearAmanFlow } from "../../../redux/features/amanFlowSlice";
import { clearAllAmanFormData } from "../../../redux/features/amanFormSlice";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import StartPage from "../../../components/newAmanPolicy/amanFlowSteps/start/StartPage";
import QuestionnairePage from "../../../components/newAmanPolicy/amanFlowSteps/questionnaire/QuestionnairePage";
import PlanSelectionPage from "../../../components/newAmanPolicy/amanFlowSteps/plan/PlanSelectionPage";
import DomesticHelperDetailsPage from "../../../components/newAmanPolicy/amanFlowSteps/details/DomesticHelperDetailsPage";
import ReviewAndConfirmPage from "../../../components/newAmanPolicy/amanFlowSteps/pay/ReviewAndConfirmPage";
import { headerStepperData, STEP_NAME_START, STEP_NAME_QUESTIONNAIRE, STEP_NAME_PLAN, STEP_NAME_DETAILS, STEP_NAME_PAY } from "./data";

const NewAmanPolicy = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep, handleNextSubstep } = useAmanFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;
  const hasAutoAdvanced = useRef(false);
  const hasCleared = useRef(false);

  // Clear form data when flow is first opened
  useEffect(() => {
    if (!hasCleared.current) {
      hasCleared.current = true;
      dispatch(clearAmanFlow());
      dispatch(clearAllAmanFormData());
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      isLoggedIn && 
      currentStep.name === "start" && 
      currentSubstep === "basic_info" && 
      !hasAutoAdvanced.current
    ) {
      hasAutoAdvanced.current = true;
      handleNextSubstep();
    }
  }, [isLoggedIn, currentStep.name, currentSubstep, handleNextSubstep]);

  const handleCloseModal = () => {
    dispatch(clearAmanFlow());
    dispatch(clearAllAmanFormData());
    setShowModal(false);
    navigate("/new-policy");
  };

  const activeStepData = useMemo(() => {
    const stepperKey = currentStep?.name;
    return headerStepperData.find((step) => step.key === stepperKey);
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep.name) {
      case STEP_NAME_START:
        return <StartPage onClose={handleCloseModal} />;
      case STEP_NAME_QUESTIONNAIRE:
        return <QuestionnairePage onClose={handleCloseModal} />;
      case STEP_NAME_PLAN:
        return <PlanSelectionPage onClose={handleCloseModal} />;
      case STEP_NAME_DETAILS:
        return <DomesticHelperDetailsPage onClose={handleCloseModal} />;
      case STEP_NAME_PAY:
        return <ReviewAndConfirmPage onClose={handleCloseModal} />;
      default:
        return <StartPage onClose={handleCloseModal} />;
    }
  };

  return (
    <Modal show={showModal}>
      <div className="flex flex-col h-full max767:h-screen">
        <div className="flex-shrink-0 sticky top-0 z-50 bg-white">
          <Header
            isPopup
            hideMobileClose
            onCloseModal={handleCloseModal}
            showStepper={true}
            steps={headerStepperData}
            activeStep={activeStepData}
          />
        </div>
        <div id="aman-back-portal" />
        <div className="mainAmanPolicyModal relative pt-5 pb-[5%] flex-1 max767:w-full max767:overflow-y-auto max767:pb-[120px] ipad:pt-[10%] ipad:h-auto ipad:overflow-y-auto max767:pt-[88px]">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={currentStep?.name || "start"}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
          {/* Stable mount point for the bottom sticky footer button, kept
              outside the animated/transformed wrapper to prevent flicker. */}
          <div id="aman-footer-portal" />
        </div>
      </div>
    </Modal>
  );
};

export default NewAmanPolicy;
